import { Pipe, PipeTransform } from '@angular/core';

export interface TimeInput {
  hours?: number;
  minutes?: number;
  seconds?: number;
}

export type TimeValue = TimeInput | string | null | undefined;

export interface TimeFormatOptions {
  /**
   * Examples:
   *  - "mm:ss"
   *  - "HH:mm:ss"
   *  - "hh:mm"
   * If not provided, fallback to verbose mode (e.g. "1h 20m 13s").
   */
  format?: string;

  /**
   * Separator for verbose mode.
   * Default: " "
   */
  separator?: string;

  /**
   * Show unit suffix in verbose mode (h/m/s).
   */
  showUnit?: boolean;

  /**
   * Force showing zero values in verbose mode.
   */
  showZero?: boolean;
}

@Pipe({ name: 'timeFormat', standalone: true })
export class TimeFormatPipe implements PipeTransform {
  transform(value: TimeValue, options?: TimeFormatOptions): string {
    if (!value)
      return options?.format ? this.formatFromSeconds(0, options.format) : '0s';

    const totalSeconds = this.toTotalSeconds(value);

    if (options?.format)
      return this.formatFromSeconds(totalSeconds, options.format);

    return this.formatVerbose(totalSeconds, options);
  }

  // -------------------------
  // Core conversion
  // -------------------------

  private toTotalSeconds(value: TimeValue): number {
    if (typeof value === 'string') return this.parseTimeString(value);

    const input = value as TimeInput;

    let hours = input.hours ?? 0;
    let minutes = input.minutes ?? 0;
    let seconds = input.seconds ?? 0;

    // Decimal hours → minutes
    if (!Number.isInteger(hours)) {
      const int = Math.floor(hours);
      minutes += (hours - int) * 60;
      hours = int;
    }

    // Decimal minutes → seconds
    if (!Number.isInteger(minutes)) {
      const int = Math.floor(minutes);
      seconds += Math.round((minutes - int) * 60);
      minutes = int;
    }

    // Decimal seconds → round
    if (!Number.isInteger(seconds)) seconds = Math.round(seconds);

    return (
      Math.floor(hours) * 3600 + Math.floor(minutes) * 60 + Math.floor(seconds)
    );
  }

  private parseTimeString(value: string): number {
    // Accepts: "hh:mm:ss", "mm:ss", "ss"
    const parts = value.split(':').map((v) => Number(v));

    if (parts.some(isNaN)) return 0;

    if (parts.length === 3) {
      const [h, m, s] = parts;
      return h * 3600 + m * 60 + s;
    }

    if (parts.length === 2) {
      const [m, s] = parts;
      return m * 60 + s;
    }

    if (parts.length === 1) return parts[0];

    return 0;
  }

  // -------------------------
  // Formatters
  // -------------------------

  private formatFromSeconds(totalSeconds: number, format: string): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const HH = String(hours).padStart(2, '0');
    const H = String(hours);
    const mm = String(minutes).padStart(2, '0');
    const m = String(minutes);
    const ss = String(seconds).padStart(2, '0');
    const s = String(seconds);

    return format
      .replace(/HH/g, HH)
      .replace(/H/g, H)
      .replace(/mm/g, mm)
      .replace(/m/g, m)
      .replace(/ss/g, ss)
      .replace(/s/g, s);
  }

  private formatVerbose(
    totalSeconds: number,
    options?: TimeFormatOptions
  ): string {
    const separator = options?.separator ?? ' ';
    const showUnit = options?.showUnit ?? true;
    const showZero = options?.showZero ?? false;

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts: string[] = [];

    if (hours > 0 || showZero) parts.push(`${hours}${showUnit ? 'h' : ''}`);

    if (minutes > 0 || showZero) parts.push(`${minutes}${showUnit ? 'm' : ''}`);

    if (seconds > 0 || parts.length === 0 || showZero)
      parts.push(`${seconds}${showUnit ? 's' : ''}`);

    return parts.join(separator);
  }
}
